import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  ContentControlTrafficFormValue,
  ContentControlTrafficSearchableKeywords,
  ContentControlDataProtectionSearchableKeywords,
  ContentControlDataProtectionFormValue,
  ContentControlNetworkMonitorSearchableKeywords,
  ContentControlWebSearchableKeywords,
  ContentControlDataProtectionRuleLightStatus,
  ContentControlWebFormValue,
} from './content-control.types';
import { ColumnValue } from '../../../../../../grid/types/common';
import {
  ContentControlAntiphishingDefaultAction,
  ContentControlApplicationRulePermission,
  ContentControlDataProtectionExclusionType,
  ContentControlDataProtectionRuleType,
  ContentControlNetworkMonitorAttackTechniqueAction,
  ContentControlNetworkMonitorAttackTechniqueName,
  ContentControlNetworkMonitorSection,
  ContentControlTrafficExclusionType,
  ContentControlWebAccessRulePermission,
  GeneralAdvancedScanClientHelloBlockType,
} from '@nirvana/shared/dtos/types/policies/common';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { Feature } from '@nirvana/shared/constants';
import { ComplexSelectionFormValue, RowFormValue } from '../../../../../../ui/gzSelectableTableRowGroup/types';
import _ from 'lodash';
import { ContentControlNetworkMonitorAttackTechniqueKey } from '../../../uiSettings/contentControl/networkMonitor/component/complex-selection-group-config';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';

export const GENERAL_ADVANCED_SETTINGS_SCAN_SSL_DEFAULT_VALUE = {
  enabled: false,
  protocols: {
    incoming: {
      rdp: false,
    },
    outgoing: {
      http: false,
      ftp: false,
      ssh: false,
    },
  },
};
export const GENERAL_ADVANCED_SETTINGS_SCAN_CLIENT_HELLO_DEFAULT_VALUE = {
  enabled: true,
  blockType: GeneralAdvancedScanClientHelloBlockType.INSERT_ERROR_PAGE,
};
export const GENERAL_ADVANCED_SETTINGS_MITM_DEFAULT_VALUE = {
  enabled: false,
  processes: [],
};

export const CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE: ContentControlTrafficFormValue = {
  uiSettings: {
    general: {
      display: {
        silentMode: {
          showBrowserToolbar: false,
        },
      },
      advanced: {
        settings: {
          scanSsl: GENERAL_ADVANCED_SETTINGS_SCAN_SSL_DEFAULT_VALUE,
          excludeFinanceDomains: false,
          browserSearchAdvisor: true,
          mitm: GENERAL_ADVANCED_SETTINGS_MITM_DEFAULT_VALUE,
          scanClientHello: GENERAL_ADVANCED_SETTINGS_SCAN_CLIENT_HELLO_DEFAULT_VALUE,
        },
      },
    },
    contentControl: {
      traffic: {
        enable: true,
        enableExclusions: false,
        exclusions: [],
      },
    },
  },
};

export const CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE: ContentControlDataProtectionFormValue = {
  uiSettings: {
    contentControl: {
      antiphishing: {
        enable: true,
        defaultAction: ContentControlAntiphishingDefaultAction.BLOCK,
        settings: {
          protectionAgainstFraud: true,
          protectionAgainstPhishing: true,
        },
      },
      traffic: {
        trafficScan: {
          incomingEmails: {
            pop3: false,
            imap: false
          },
          outgoingEmails: {
            smtp: false
          },
          incomingAndOutgoingEmails: {
            mapi: false
          },
          webTraffic: true,
          emailTraffic: false,
        },
      },
    },
  },
};

export const CONTENT_CONTROL_NETWORK_MONITOR_DEFAULT_VALUE: ContentControlNetworkMonitorSection = {
  enable: true,
  inspectRdpTraffic: false,
  attackTechniques: [],
  serverTrafficScan: {
    enabled: false,
    inspectEncryptedDomainControllerTraffic: false,
  },
};

export const CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE: ContentControlWebFormValue = {
  uiSettings: {
    contentControl: {
      webAccess: {
        enable: false,
        profileType: 3,
        useExceptions: true,
        webRules: [],
        scheduler: [],
      },
      webCategoriesFilter: {
        profileType: 3,
        scheduleId: null,
      },
      application: {
        enable: false,
        rules: [],
      },
      dataProtection: {
        enable: false,
        rules: [],
        exclusions: [],
      },
    },
  },
};

// get exclusion item type grid values
export function getLabelByExclusionItemType(type: ContentControlTrafficExclusionType): string {
  switch (type) {
    case ContentControlTrafficExclusionType.IP:
      return translateFn('IP/mask');
    case ContentControlTrafficExclusionType.URL:
      return translateFn('URL');
    case ContentControlTrafficExclusionType.APPLICATION:
      return translateFn('Application');
  }
}
export function getExclusionItemTypeGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByExclusionItemType(ContentControlTrafficExclusionType.IP),
      value: ContentControlTrafficExclusionType.IP,
    },
    {
      label: getLabelByExclusionItemType(ContentControlTrafficExclusionType.URL),
      value: ContentControlTrafficExclusionType.URL,
    },
    {
      label: getLabelByExclusionItemType(ContentControlTrafficExclusionType.APPLICATION),
      value: ContentControlTrafficExclusionType.APPLICATION,
    },
  ];
}
export function getExclusionItemTypeFormOptions(): Array<SelectOption> {
  return [
    {
      title: getLabelByExclusionItemType(ContentControlTrafficExclusionType.IP),
      value: ContentControlTrafficExclusionType.IP,
    },
    {
      title: getLabelByExclusionItemType(ContentControlTrafficExclusionType.URL),
      value: ContentControlTrafficExclusionType.URL,
    },
    {
      title: getLabelByExclusionItemType(ContentControlTrafficExclusionType.APPLICATION),
      value: ContentControlTrafficExclusionType.APPLICATION,
    },
  ];
}

export function getDefaultActionFormOptions(): Array<SelectOption> {
  const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  const defaultActionOptions = [
    {
      title: translateFn('Report only'),
      value: ContentControlAntiphishingDefaultAction.REPORT_ONLY,
    },
  ];

  if (!isCompanionStandalone) {
    defaultActionOptions.push({
      title: translateFn('Block'),
      value: ContentControlAntiphishingDefaultAction.BLOCK,
    });
  }

  return defaultActionOptions;
}

export function getAttackTechniquesDefaultValue(): ComplexSelectionFormValue<
  ContentControlNetworkMonitorAttackTechniqueKey,
  ContentControlNetworkMonitorAttackTechniqueAction
> {
  const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  const defaultValue: RowFormValue<
    ContentControlNetworkMonitorAttackTechniqueName,
    ContentControlNetworkMonitorAttackTechniqueAction
  > = {
    enabled: true,
    column: isCompanionStandalone
      ? ContentControlNetworkMonitorAttackTechniqueAction.REPORT_ONLY
      : ContentControlNetworkMonitorAttackTechniqueAction.BLOCK_PROCESS,
  };

  return {
    [ContentControlNetworkMonitorAttackTechniqueKey.INITIAL_ACCESS]: _.clone(defaultValue),
    [ContentControlNetworkMonitorAttackTechniqueKey.CREDENTIAL_ACCESS]: _.clone(defaultValue),
    [ContentControlNetworkMonitorAttackTechniqueKey.LATERAL_MOVEMENT]: _.clone(defaultValue),
    [ContentControlNetworkMonitorAttackTechniqueKey.CRIMEWARE]: _.clone(defaultValue),
    [ContentControlNetworkMonitorAttackTechniqueKey.DISCOVERY]: _.clone(defaultValue),
  };
}

// web access - exclusion - permission
export function getLabelByExclusionItemPermission(permission: ContentControlWebAccessRulePermission): string {
  switch (permission) {
    case ContentControlWebAccessRulePermission.BLOCK:
      return translateFn('Block');
    case ContentControlWebAccessRulePermission.ALLOW:
      return translateFn('Allow');
  }
}
export function getWebAccessPermissionFormOptions(): ColumnValue[] | ListItem[] {
  return [
    {
      label: getLabelByExclusionItemPermission(ContentControlWebAccessRulePermission.BLOCK),
      value: ContentControlWebAccessRulePermission.BLOCK,
    },
    {
      label: getLabelByExclusionItemPermission(ContentControlWebAccessRulePermission.ALLOW),
      value: ContentControlWebAccessRulePermission.ALLOW,
    },
  ];
}

export function getLabelByRuleType(type: ContentControlDataProtectionRuleType): string {
  switch (type) {
    case ContentControlDataProtectionRuleType.ADDRESS:
      return translateFn('Address');
    case ContentControlDataProtectionRuleType.BANKACCNO:
      return translateFn('Bank Account Number');
    case ContentControlDataProtectionRuleType.CCNO:
      return translateFn('Credit Card Number');
    case ContentControlDataProtectionRuleType.EMAIL:
      return translateFn('Email');
    case ContentControlDataProtectionRuleType.NAME:
      return translateFn('Name');
    case ContentControlDataProtectionRuleType.PASSWORD:
      return translateFn('Password');
    case ContentControlDataProtectionRuleType.PIN:
      return translateFn('PIN');
    case ContentControlDataProtectionRuleType.SSN:
      return translateFn('SSN');
    case ContentControlDataProtectionRuleType.PHONE:
      return translateFn('Telephone');
  }
}
export function getDataProtectionRuleTypeGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.ADDRESS),
      value: ContentControlDataProtectionRuleType.ADDRESS,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.BANKACCNO),
      value: ContentControlDataProtectionRuleType.BANKACCNO,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.CCNO),
      value: ContentControlDataProtectionRuleType.CCNO,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.EMAIL),
      value: ContentControlDataProtectionRuleType.EMAIL,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.NAME),
      value: ContentControlDataProtectionRuleType.NAME,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.PASSWORD),
      value: ContentControlDataProtectionRuleType.PASSWORD,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.PIN),
      value: ContentControlDataProtectionRuleType.PIN,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.SSN),
      value: ContentControlDataProtectionRuleType.SSN,
    },
    {
      label: getLabelByRuleType(ContentControlDataProtectionRuleType.PHONE),
      value: ContentControlDataProtectionRuleType.PHONE,
    },
  ];
}
export function getDataProtectionRuleTypeDropdownOptions(): Array<SelectOption> {
  return [
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.ADDRESS),
      value: ContentControlDataProtectionRuleType.ADDRESS,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.BANKACCNO),
      value: ContentControlDataProtectionRuleType.BANKACCNO,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.CCNO),
      value: ContentControlDataProtectionRuleType.CCNO,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.EMAIL),
      value: ContentControlDataProtectionRuleType.EMAIL,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.NAME),
      value: ContentControlDataProtectionRuleType.NAME,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.PASSWORD),
      value: ContentControlDataProtectionRuleType.PASSWORD,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.PIN),
      value: ContentControlDataProtectionRuleType.PIN,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.SSN),
      value: ContentControlDataProtectionRuleType.SSN,
    },
    {
      title: getLabelByRuleType(ContentControlDataProtectionRuleType.PHONE),
      value: ContentControlDataProtectionRuleType.PHONE,
    },
  ];
}

export function getLabelByExclusionType(type: ContentControlDataProtectionExclusionType): string {
  switch (type) {
    case ContentControlDataProtectionExclusionType.WEB:
      return translateFn('Web address');
    case ContentControlDataProtectionExclusionType.EMAIL:
      return translateFn('Email address');
  }
}
export function getDataProtectionExclusionTypeGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByExclusionType(ContentControlDataProtectionExclusionType.WEB),
      value: ContentControlDataProtectionExclusionType.WEB,
    },
    {
      label: getLabelByExclusionType(ContentControlDataProtectionExclusionType.EMAIL),
      value: ContentControlDataProtectionExclusionType.EMAIL,
    },
  ];
}
export function getDataProtectionExclusionTypeDropdownOptions(): Array<SelectOption> {
  return [
    {
      title: getLabelByExclusionType(ContentControlDataProtectionExclusionType.WEB),
      value: ContentControlDataProtectionExclusionType.WEB,
    },
    {
      title: getLabelByExclusionType(ContentControlDataProtectionExclusionType.EMAIL),
      value: ContentControlDataProtectionExclusionType.EMAIL,
    },
  ];
}

export function getLabelByApplicationRuleItemPermission(permission: ContentControlApplicationRulePermission): string {
  switch (permission) {
    case ContentControlApplicationRulePermission.BLOCK:
      return translateFn('Block');
    case ContentControlApplicationRulePermission.SCHEDULED:
      return translateFn('Scheduled');
    case ContentControlApplicationRulePermission.ALLOW:
      return translateFn('Allow');
  }
}
export function getApplicationRulePermissionGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByApplicationRuleItemPermission(ContentControlApplicationRulePermission.BLOCK),
      value: ContentControlApplicationRulePermission.BLOCK,
    },
    {
      label: getLabelByApplicationRuleItemPermission(ContentControlApplicationRulePermission.SCHEDULED),
      value: ContentControlApplicationRulePermission.SCHEDULED,
    },
    {
      label: getLabelByApplicationRuleItemPermission(ContentControlApplicationRulePermission.ALLOW),
      value: ContentControlApplicationRulePermission.ALLOW,
    },
  ];
}

// Status section
export function getLabelByContentControlLightRuleItemStatus(
  status: ContentControlDataProtectionRuleLightStatus
): string {
  switch (status) {
    case ContentControlDataProtectionRuleLightStatus.ENABLED:
      return translateFn('Enabled');
    case ContentControlDataProtectionRuleLightStatus.DISABLED:
      return translateFn('Disabled');
  }
}
export function getContentControlLightRuleItemStatusGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByContentControlLightRuleItemStatus(ContentControlDataProtectionRuleLightStatus.ENABLED),
      value: ContentControlDataProtectionRuleLightStatus.ENABLED,
    },
    {
      label: getLabelByContentControlLightRuleItemStatus(ContentControlDataProtectionRuleLightStatus.DISABLED),
      value: ContentControlDataProtectionRuleLightStatus.DISABLED,
    },
  ];
}

// searchable keywords
export function getContentControlTrafficSearchableKeywords(): {
  [type in ContentControlTrafficSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ContentControlTrafficSearchableKeywords.NetworkProtection]: {
      translatedLabel: translateFn('Network Protection'),
      label: 'Network Protection',
    },
    [ContentControlTrafficSearchableKeywords.GeneralSettings]: {
      translatedLabel: translateFn('General settings'),
      label: 'General settings',
    },
    [ContentControlTrafficSearchableKeywords.InterceptEncryptedTraffic]: {
      translatedLabel: translateFn('Intercept Encrypted Traffic'),
      label: 'Intercept Encrypted Traffic',
    },
    [ContentControlTrafficSearchableKeywords.ScanHttps]: {
      translatedLabel: translateFn('Scan HTTPS'),
      label: 'Scan HTTPS',
    },
    [ContentControlTrafficSearchableKeywords.Additional]: {
      translatedLabel: translateFn('Additional processes'),
      label: 'Additional processes',
    },
    [ContentControlTrafficSearchableKeywords.ScanFtps]: {
      translatedLabel: translateFn('Scan FTPS'),
      label: 'Scan FTPS',
    },
    [ContentControlTrafficSearchableKeywords.ScanSsh]: {
      translatedLabel: translateFn('Scan SCP/SSH'),
      label: 'Scan SCP/SSH',
    },
    [ContentControlTrafficSearchableKeywords.ScanImaps]: {
      translatedLabel: translateFn('Scan IMAPS'),
      label: 'Scan IMAPS',
    },
    [ContentControlTrafficSearchableKeywords.ScanMapi]: {
      translatedLabel: translateFn('Scan MAPI'),
      label: 'Scan MAPI',
    },
    [ContentControlTrafficSearchableKeywords.ScanPop3]: {
      translatedLabel: translateFn('Scan POP3S'),
      label: 'Scan POP3S',
    },
    [ContentControlTrafficSearchableKeywords.ScanSmtp]: {
      translatedLabel: translateFn('Scan SMTPS'),
      label: 'Scan SMTPS',
    },
    [ContentControlTrafficSearchableKeywords.ExcludeFinanceDomains]: {
      translatedLabel: translateFn('Exclude finance domains'),
      label: 'Exclude finance domains',
    },
    [ContentControlTrafficSearchableKeywords.InterceptTls]: {
      translatedLabel: translateFn('Intercept TLS handshake'),
      label: 'Intercept TLS handshake',
    },
    [ContentControlTrafficSearchableKeywords.AccessDenied]: {
      translatedLabel: translateFn('Respond with an Access Denied page'),
      label: 'Respond with an Access Denied page',
    },
    [ContentControlTrafficSearchableKeywords.ResetConnection]: {
      translatedLabel: translateFn('Reset connection'),
      label: 'Reset connection',
    },
    [ContentControlTrafficSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
  };
}
export function getContentControlDataProtectionSearchableKeywords(): {
  [type in ContentControlDataProtectionSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ContentControlDataProtectionSearchableKeywords.Antiphishing]: {
      translatedLabel: translateFn('Antiphishing'),
      label: 'Antiphishing',
    },
    [ContentControlDataProtectionSearchableKeywords.DefaultAction]: {
      translatedLabel: translateFn('Default action for suspicious webpages'),
      label: 'Default action for suspicious webpages',
    },
    [ContentControlDataProtectionSearchableKeywords.ProtectionAgainstFraud]: {
      translatedLabel: translateFn('Protection against fraud'),
      label: 'Protection against fraud',
    },
    [ContentControlDataProtectionSearchableKeywords.ProtectionAgainstPhishing]: {
      translatedLabel: translateFn('Protection against phishing'),
      label: 'Protection against phishing',
    },
    [ContentControlDataProtectionSearchableKeywords.WebTrafficScan]: {
      translatedLabel: translateFn('Web Traffic Scan'),
      label: 'Web Traffic Scan',
    },
    [ContentControlDataProtectionSearchableKeywords.EmailTrafficScan]: {
      translatedLabel: translateFn('Email Traffic Scan'),
      label: 'Email Traffic Scan',
    },
    [ContentControlDataProtectionSearchableKeywords.Pop3]: {
      translatedLabel: translateFn('Incoming emails (POP3)'),
      label: 'Incoming emails (POP3)',
    },
    [ContentControlDataProtectionSearchableKeywords.Smtp]: {
      translatedLabel: translateFn('Outgoing emails (SMTP)'),
      label: 'Outgoing emails (SMTP)',
    },
    [ContentControlDataProtectionSearchableKeywords.Imap]: {
      translatedLabel: translateFn('Incoming emails (IMAP)'),
      label: 'Incoming emails (IMAP)',
    },
    [ContentControlDataProtectionSearchableKeywords.Mapi]: {
      translatedLabel: translateFn('Incoming and outgoing (MAPI)'),
      label: 'Incoming and outgoing (MAPI)',
    },
  };
}

export function getContentControlNetworkMonitorSearchableKeywords(): {
  [type in ContentControlNetworkMonitorSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ContentControlNetworkMonitorSearchableKeywords.NetworkAttacksDefense]: {
      translatedLabel: translateFn('Network Attack Defense'),
      label: 'Network Attack Defense',
    },
    [ContentControlNetworkMonitorSearchableKeywords.InspectRdpTraffic]: {
      translatedLabel: translateFn('Inspect RDP traffic'),
      label: 'Inspect RDP traffic',
    },
    [ContentControlNetworkMonitorSearchableKeywords.AttCkTechniques]: {
      translatedLabel: translateFn('ATT&CK Techniques'),
      label: 'ATT&CK Techniques',
    },
    [ContentControlNetworkMonitorSearchableKeywords.InitialAccess]: {
      translatedLabel: translateFn('Initial Access'),
      label: 'Initial Access',
    },
    [ContentControlNetworkMonitorSearchableKeywords.CredentialAccess]: {
      translatedLabel: translateFn('Credential Access'),
      label: 'Credential Access',
    },
    [ContentControlNetworkMonitorSearchableKeywords.Discovery]: {
      translatedLabel: translateFn('Discovery'),
      label: 'Discovery',
    },
    [ContentControlNetworkMonitorSearchableKeywords.LateralMovement]: {
      translatedLabel: translateFn('Lateral Movement'),
      label: 'Lateral Movement',
    },
    [ContentControlNetworkMonitorSearchableKeywords.Crimeware]: {
      translatedLabel: translateFn('Crimeware'),
      label: 'Crimeware',
    },
    [ContentControlNetworkMonitorSearchableKeywords.ResetToDefault]: {
      translatedLabel: translateFn('Reset to default'),
      label: 'Reset to default',
    },
    [ContentControlNetworkMonitorSearchableKeywords.ServerTrafficScan]: {
      translatedLabel: translateFn('Server traffic scan'),
      label: 'Server traffic scan',
    },
    [ContentControlNetworkMonitorSearchableKeywords.InspectEncryptedDomainControllerTraffic]: {
      translatedLabel: translateFn('Inspect encrypted domain controller traffic'),
      label: 'Inspect encrypted domain controller traffic',
    }
  };
}

export function getContentControlWebSearchableKeywords(): {
  [type in ContentControlWebSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ContentControlWebSearchableKeywords.WebAccessControl]: {
      translatedLabel: translateFn('Web Access Control'),
      label: 'Web Access Control',
    },
    [ContentControlWebSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [ContentControlWebSearchableKeywords.ApplicationBlacklist]: {
      translatedLabel: translateFn('Application Blacklist'),
      label: 'Application Blacklist',
    },
    [ContentControlWebSearchableKeywords.DataProtection]: {
      translatedLabel: translateFn('Data Protection'),
      label: 'Data Protection',
    },
  };
}
